/**
 * D1 — resolver o "convite quebrado" colando o link novo, no próprio card do incidente.
 *
 * Fluxo (spec de UX, seção 4):
 *  1. pendente: botão em loading, "Conferindo o link…";
 *  2. sucesso: toast "Link atualizado. O grupo voltou ao rodízio.";
 *  3. erro com causa clara ("Este link é de outro grupo (Ofertas RJ 3)", "Link expirado ou
 *     inválido");
 *  4. provedor sem resposta: "Não conseguimos confirmar agora. O incidente continua aberto." —
 *     e NÃO resolve (ausência de resposta não vale como resposta).
 *
 * "Ignorar" só recolhe o campo: o incidente continua como está (quem reconhece ou resolve é o
 * botão do card). A ação principal é `neutral`, como as outras da lista — a cor de ação não é
 * gasta numa lista de avisos.
 */
import { useState } from 'react';
import { Button, Field, FormAlert, Input, useToast } from '@mgdlead/ui';
import { Check } from 'lucide-react';

import { pasteInviteFeedback } from '../../../features/group/invite-paste-ui';
import { usePasteGroupInvite } from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';

export interface InviteFixFormProps {
  workspaceId: string;
  groupId: string;
  incidentTitle: string;
}

export function InviteFixForm({ workspaceId, groupId, incidentTitle }: InviteFixFormProps) {
  const { toast } = useToast();
  const paste = usePasteGroupInvite();
  const [link, setLink] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [recolhido, setRecolhido] = useState(false);

  if (recolhido) {
    return (
      <Button variant="ghost" size="sm" onClick={() => setRecolhido(false)} className="self-start">
        Colar o novo link do grupo
      </Button>
    );
  }

  const enviar = () => {
    setErro(null);
    paste.mutate(
      { workspaceId, groupId, link: link.trim() },
      {
        onSuccess: (result) => {
          const feedback = pasteInviteFeedback(result);
          if (feedback.kind === 'success') {
            setLink('');
            toast({ title: feedback.title, description: feedback.description, tone: 'success' });
          } else {
            setErro(feedback.message);
          }
        },
        onError: (error: unknown) => setErro(humanizeError(error)),
      },
    );
  };

  return (
    <form
      className="flex max-w-xl flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (link.trim().length > 0 && !paste.isPending) enviar();
      }}
      aria-label={`Resolver com o novo link: ${incidentTitle}`}
    >
      <Field
        label="Cole o novo link do grupo"
        hint="No WhatsApp: dados do grupo › Convidar via link."
      >
        {(control) => (
          <Input
            {...control}
            type="url"
            inputMode="url"
            autoComplete="off"
            placeholder="https://chat.whatsapp.com/…"
            maxLength={200}
            value={link}
            invalid={erro !== null}
            onChange={(event) => {
              setLink(event.target.value);
              if (erro !== null) setErro(null);
            }}
          />
        )}
      </Field>

      {erro !== null ? <FormAlert>{erro}</FormAlert> : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="submit"
          variant="neutral"
          size="sm"
          leadingIcon={<Check />}
          loading={paste.isPending}
          loadingLabel="Conferindo o link…"
          disabled={link.trim().length === 0}
          className="max-md:flex-1"
        >
          Validar e resolver
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          title="Esconde o campo. O incidente continua aberto."
          disabled={paste.isPending}
          onClick={() => {
            setErro(null);
            setRecolhido(true);
          }}
        >
          Ignorar
        </Button>
      </div>
    </form>
  );
}
